import React, { useState, useRef } from 'react';
import EmojiPicker from './EmojiPicker';
import { getEmojiDisplay } from '../utils/emojiMapper';
import './MessageReactions.css';

function MessageReactions({ reactions = [], messageId, onAddReaction, onRemoveReaction, currentUserId, users = {} }) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hoveredReaction, setHoveredReaction] = useState(null);
  const addReactionButtonRef = useRef(null);

  const handleReactionClick = (reaction) => {
    const userReacted = reaction.users?.includes(currentUserId);
    if (userReacted) {
      onRemoveReaction(messageId, reaction.name);
    } else {
      onAddReaction(messageId, reaction.name);
    }
  };

  const handleAddNewReaction = (emoji) => {
    onAddReaction(messageId, emoji);
    setShowEmojiPicker(false);
  };

  const getUserNames = (userIds = []) => {
    return userIds.map(userId => {
      const user = users[userId];
      if (!user) return 'Unknown User';
      if (userId === currentUserId) return 'You';
      return user.real_name || user.name || 'Unknown User';
    });
  };

  const formatTooltipText = (reaction) => {
    const userNames = getUserNames(reaction.users);
    if (userNames.length === 0) return '';

    if (userNames.length === 1) {
      return userNames[0];
    } else if (userNames.length === 2) {
      return `${userNames[0]} and ${userNames[1]}`;
    } else if (userNames.length === 3) {
      return `${userNames[0]}, ${userNames[1]}, and ${userNames[2]}`;
    } else {
      const displayNames = userNames.slice(0, 3);
      const remaining = userNames.length - 3;
      return `${displayNames.join(', ')}, and ${remaining} other${remaining > 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="message-reactions">
      {reactions.map((reaction) => {
        const userReacted = reaction.users?.includes(currentUserId);
        const tooltipText = formatTooltipText(reaction);
        const isHovered = hoveredReaction === reaction.name;

        return (
          <div
            key={reaction.name}
            className="reaction-container"
            onMouseEnter={() => setHoveredReaction(reaction.name)}
            onMouseLeave={() => setHoveredReaction(null)}
          >
            <button
              className={`reaction-pill ${userReacted ? 'user-reacted' : ''}`}
              onClick={() => handleReactionClick(reaction)}
            >
              <span className="reaction-emoji">{getEmojiDisplay(reaction.name)}</span>
              <span className="reaction-count">{reaction.count || reaction.users?.length || 0}</span>
            </button>
            {isHovered && tooltipText && (
              <div className="reaction-tooltip">
                <div className="reaction-tooltip-content">
                  <span className="reaction-tooltip-emoji">{getEmojiDisplay(reaction.name)}</span>
                  <span className="reaction-tooltip-text">{tooltipText}</span>
                </div>
                <div className="reaction-tooltip-arrow"></div>
              </div>
            )}
          </div>
        );
      })}

      <div className="add-reaction-container">
        <button
          ref={addReactionButtonRef}
          className="add-reaction-btn"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          title="Add reaction"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8s2.91 6.5 6.5 6.5 6.5-2.91 6.5-6.5S11.59 1.5 8 1.5zm0 11.75c-2.9 0-5.25-2.35-5.25-5.25S5.1 2.75 8 2.75 13.25 5.1 13.25 8 10.9 13.25 8 13.25z"/>
            <path d="M5.5 7.25a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 7.25a.75.75 0 100-1.5.75.75 0 000 1.5zM8 11c1.24 0 2.31-.71 2.83-1.75H5.17C5.69 10.29 6.76 11 8 11z"/>
          </svg>
        </button>

      </div>

      <EmojiPicker
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onEmojiSelect={handleAddNewReaction}
        referenceElement={addReactionButtonRef.current}
      />
    </div>
  );
}

export default MessageReactions;